"use client"

// Imports de bibliothèques externes
import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { 
  FiDollarSign, 
  FiCalendar, 
  FiClock, 
  FiCheckCircle, 
  FiDownload, 
  FiPlus, 
  FiArrowRight, 
  FiTrendingUp,
  FiCreditCard,
  FiX,
  FiAlertCircle,
  FiFilter,
  FiSearch,
  FiRefreshCw,
  FiSettings,
  FiEye,
  FiZap,
  FiTarget,
  FiBarChart2,
  FiActivity,
  FiNavigation
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

// Imports d'utilitaires et de contexte
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  getPaymentMethods, 
  requestPayout, 
  getPaymentStats,
  getTransactionHistory 
} from '../../services/paymentService';

// Imports de composants
import AddPaymentMethodModal from './AddPaymentMethodModal';
import TestEnv from '../TestEnv';

// Fonction utilitaire pour formater les montants en TND
const formatTND = (value) => {
  return new Intl.NumberFormat('fr-TN', {
    style: 'currency',
    currency: 'TND',
    minimumFractionDigits: 2,
  }).format(value);
};

// Fonction utilitaire pour formater les dates avec gestion des erreurs améliorée
const formatDate = (dateInput) => {
  try {
    if (!dateInput) {
      console.debug('Aucune date fournie');
      return '--';
    }
    
    let date;
    if (typeof dateInput === 'string') {
      // Si la chaîne contient un espace, c'est probablement une date PostgreSQL
      if (dateInput.includes(' ')) {
        // Remplacer l'espace par 'T' pour le format ISO-8601
        const isoString = dateInput.replace(' ', 'T');
        date = new Date(isoString);
      } else if (dateInput.includes('-') && dateInput.includes(':')) {
        // Essayer de parser comme une date ISO-8601
        date = new Date(dateInput);
      } else {
        // Essayer de parser comme un timestamp
        date = new Date(parseInt(dateInput));
      }
    } else {
      // Gérer les objets Date et autres formats
      date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    }
    
    if (isNaN(date.getTime())) {
      console.debug('Date invalide:', dateInput);
      return '--';
    }
    
    // Formater la date en français
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(date);
  } catch (error) {
    console.error('Erreur de formatage de la date:', error);
    console.debug('Valeur de la date:', dateInput);
    return '--';
  }
};


if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
  console.error('ERREUR: REACT_APP_STRIPE_PUBLISHABLE_KEY n\'est pas définie');
}

// Initialisation de Stripe avec votre clé publique
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

// --- Composants ---
// formatTND est déjà importé depuis '../../utils/formatters'

const LoadingSkeleton = ({ count = 1 }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <tr key={i}>
        <td colSpan="5" className="p-4">
          <div className="h-8 bg-slate-700 rounded animate-pulse"></div>
        </td>
      </tr>
    ))}
  </>
);

const StatCard = ({ icon, label, value, trend, colorClass, gradient, delay = 0 }) => (
    <motion.div 
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, delay, ease: "easeOut" }}
        whileHover={{ y: -5, scale: 1.02 }}
        className="bg-gradient-to-br from-slate-800/90 via-slate-800/70 to-slate-900/90 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-6 shadow-2xl hover:shadow-amber-500/10 hover:border-amber-500/30 transition-all duration-300 group"
    >
        <div className="flex items-center justify-between mb-6">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center bg-gradient-to-br ${gradient} shadow-lg ring-2 ring-amber-500/20 group-hover:ring-amber-500/40 transition-all duration-300`}>
                {icon}
            </div>
            <div className="text-right">
                <p className="text-sm font-semibold text-slate-300 tracking-wide">{label}</p>
        </div>
        </div>
        <div className="space-y-2">
            <p className="text-4xl font-bold text-white tracking-tight">{value}</p>
            <p className="text-xs font-medium text-slate-400">{trend}</p>
        </div>
    </motion.div>
);

const StatusBadge = ({ status }) => {
    const statusMap = {
        'Payé': { 
            bg: 'bg-gradient-to-r from-green-500/20 to-emerald-500/20', 
            text: 'text-green-400', 
            border: 'border-green-500/30' 
        },
        'En attente': { 
            bg: 'bg-gradient-to-r from-amber-500/20 to-yellow-500/20', 
            text: 'text-amber-400', 
            border: 'border-amber-500/30' 
        },
        'Échoué': { 
            bg: 'bg-gradient-to-r from-red-500/20 to-red-600/20', 
            text: 'text-red-400', 
            border: 'border-red-500/30' 
        },
    };
    const config = statusMap[status] || { bg: 'bg-gradient-to-r from-gray-500/20 to-gray-600/20', text: 'text-gray-400', border: 'border-gray-500/30' };
    
    return (
        <span className={`px-3 py-1.5 text-xs font-bold rounded-full border ${config.bg} ${config.text} ${config.border}`}>
            {status}
        </span>
    );
};


export default function DemPaiements() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const navigate = useNavigate();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [stripeError, setStripeError] = useState(null);
  const [paymentStats, setPaymentStats] = useState({
    totalEarned: 0,
    pendingPayouts: 0,
    completedJobs: 0
  });
  const [summaryData, setSummaryData] = useState({
    monthlyRevenue: 0,
    pendingPayout: 0,
    nextPayoutDate: '--/--/----',
  });
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('');
  const [processingPayout, setProcessingPayout] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const subscriptionPlanId = searchParams.get('subscription_plan');
  const [showAddMethodModal, setShowAddMethodModal] = useState(!!subscriptionPlanId);

  // Charger les données de paiement du déménageur
  const loadData = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Charger les données en parallèle
      const [statsResponse, transactionsResponse, paymentMethodsResponse] = await Promise.allSettled([
        getPaymentStats(user.id),
        getTransactionHistory(user.id, { 
          page: pagination.page, 
          limit: pagination.limit 
        }),
        getPaymentMethods(user.id)
      ]);
      
      // Gérer la réponse des statistiques
      let stats = {};
      if (statsResponse.status === 'fulfilled') {
        // Vérifier si la réponse est déjà formatée ou si elle contient une propriété data
        stats = statsResponse.value?.data || statsResponse.value || {};
      } else {
        console.error('Erreur lors du chargement des statistiques:', statsResponse.reason);
      }
      
      // Mettre à jour les statistiques de paiement avec des valeurs par défaut sûres
      const totalEarned = parseFloat(stats?.totalEarned || stats?.total_earned || 0);
      const pendingPayouts = parseFloat(stats?.pendingPayouts || stats?.pending_payouts || 0);
      const completedJobs = parseInt(stats?.completedJobs || stats?.completed_jobs || 0, 10);
      
      // Gérer la réponse des transactions
      let transactionsData = { data: [], total: 0, totalPages: 1 };
      if (transactionsResponse.status === 'fulfilled') {
        transactionsData = transactionsResponse.value?.data || transactionsResponse.value || { data: [], total: 0, totalPages: 1 };
      } else {
        console.error('Erreur lors du chargement des transactions:', transactionsResponse.reason);
      }
      
      // Gérer la réponse des méthodes de paiement
      let paymentMethods = [];
      if (paymentMethodsResponse.status === 'fulfilled') {
        paymentMethods = Array.isArray(paymentMethodsResponse.value?.data) 
          ? paymentMethodsResponse.value.data 
          : Array.isArray(paymentMethodsResponse.value) 
            ? paymentMethodsResponse.value 
            : [];
      } else {
        console.error('Erreur lors du chargement des méthodes de paiement:', paymentMethodsResponse.reason);
      }
      
      // Mettre à jour les états avec les données récupérées
      setPaymentStats({
        totalEarned,
        pendingPayouts,
        completedJobs
      });
      
      // Mettre à jour les données récapitulatives avec des valeurs par défaut sûres
      setSummaryData({
        monthlyRevenue: totalEarned,
        pendingPayout: pendingPayouts,
        nextPayoutDate: stats.nextPayoutDate || stats.next_payout_date || '--/--/----',
      });
      
      // Mettre à jour les transactions avec vérification de la structure des données
      const safeTransactions = Array.isArray(transactionsData.data) 
        ? transactionsData.data 
        : Array.isArray(transactionsData) 
          ? transactionsData 
          : [];
      
      console.log('Données brutes des transactions:', safeTransactions);
      
      // Normaliser les noms de champs pour s'assurer que createdAt est correctement défini
      const normalizedTransactions = safeTransactions.map(tx => ({
        ...tx,
        // S'assurer que createdAt est défini, en essayant différentes variantes de noms de champs
        createdAt: tx.createdAt || tx.created_at || tx.date || new Date().toISOString(),
        // Normaliser également les autres champs importants
        amount: tx.amount || 0,
        type: tx.type || 'paiement',
        reference: tx.reference || `TRX-${tx.id?.substring(0, 8) || 'N/A'}`
      }));
      
      console.log('Transactions normalisées:', normalizedTransactions);
      
      setTransactions(normalizedTransactions);
      setPaymentMethods(paymentMethods);
      
      // Mettre à jour la pagination avec des valeurs par défaut sûres
      const total = typeof transactionsData.total === 'number' 
        ? transactionsData.total 
        : safeTransactions.length;
        
      const totalPages = typeof transactionsData.totalPages === 'number'
        ? transactionsData.totalPages
        : Math.ceil(total / (pagination.limit || 10));
      
      setPagination(prev => ({
        ...prev,
        total,
        totalPages: totalPages < 1 ? 1 : totalPages,
      }));
      
    } catch (error) {
      console.error('Erreur lors du chargement des données de paiement:', error);
      
      // Extraire le message d'erreur de différentes façons selon la structure de l'erreur
      let errorMessage = 'Une erreur est survenue lors du chargement des données';
      
      if (error.response) {
        // Erreur de réponse HTTP
        errorMessage = error.response.data?.message || error.response.statusText || errorMessage;
      } else if (error.request) {
        // La requête a été faite mais aucune réponse n'a été reçue
        errorMessage = 'Pas de réponse du serveur. Vérifiez votre connexion Internet.';
      } else if (error.message) {
        // Une erreur s'est produite lors de la configuration de la requête
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
      
      // Réinitialisation des états en cas d'erreur critique
      setPaymentStats({
        totalEarned: 0,
        pendingPayouts: 0,
        completedJobs: 0
      });
      setSummaryData({
        monthlyRevenue: 0,
        pendingPayout: 0,
        nextPayoutDate: '--/--/----',
      });
      setTransactions([]);
      setPaymentMethods([]);
    } finally {
      setLoading(false);
    }
  };

  // Charger les données au montage et quand la pagination change
  useEffect(() => {
    loadData();
  }, [user, pagination.page, pagination.limit]);

  // Gestion de la demande de virement
  const handleRequestPayout = async () => {
    if (!payoutAmount || !selectedMethod) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    try {
      setProcessingPayout(true);
      const amount = parseFloat(payoutAmount);
      
      // Validation du montant
      if (isNaN(amount) || amount <= 0) {
        toast.error('Veuillez entrer un montant valide');
        return;
      }
      
      // Vérification du solde disponible
      const availableBalance = paymentStats.pendingPayouts;
      if (amount > availableBalance) {
        toast.error(`Le montant demandé dépasse votre solde disponible de ${formatTND(availableBalance)}`);
        return;
      }
      
      // Vérification du montant minimum
      const minimumPayout = 50; // 50 TND minimum de retrait
      if (amount < minimumPayout) {
        toast.error(`Le montant minimum de retrait est de ${formatTND(minimumPayout)}`);
        return;
      }
      
      // Envoi de la demande de virement
      await requestPayout({
        userId: user.id,
        amount,
        paymentMethodId: selectedMethod
      });
      
      // Mise à jour de l'interface utilisateur
      toast.success('Votre demande de virement a été enregistrée avec succès');
      
      // Réinitialisation du formulaire et fermeture de la modale
      setPayoutAmount('');
      setSelectedMethod('');
      setShowPayoutModal(false);
      
      // Rechargement des données
      await loadData();
      
    } catch (error) {
      console.error('Erreur lors de la demande de virement:', error);
      const errorMessage = error.response?.data?.message || 'Une erreur est survenue lors de la demande de virement';
      toast.error(errorMessage);
    } finally {
      setProcessingPayout(false);
    }
  };

  // Fonction de filtrage des transactions
  const filteredTransactions = transactions.filter(transaction => {
    // Filtre par terme de recherche
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        transaction.reference?.toLowerCase().includes(searchLower) ||
        transaction.id?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Filtre par statut
    if (statusFilter !== "all") {
      if (transaction.status !== statusFilter) return false;
    }

    // Filtre par type
    if (typeFilter !== "all") {
      if (transaction.type !== typeFilter) return false;
    }

    // Filtre par date (simplifié)
    if (dateFilter !== "all") {
      const transactionDate = new Date(transaction.createdAt);
      const now = new Date();
      
      switch (dateFilter) {
        case "today":
          if (transactionDate.toDateString() !== now.toDateString()) return false;
          break;
        case "week":
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          if (transactionDate < weekAgo) return false;
          break;
        case "month":
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          if (transactionDate < monthAgo) return false;
          break;
        default:
          break;
      }
    }

    return true;
  });

  // Fonction de rafraîchissement
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await loadData();
      toast.success("Données mises à jour !");
    } catch (error) {
      toast.error("Erreur lors du rafraîchissement");
    } finally {
      setIsRefreshing(false);
    }
  };

  // Gestion du changement de page
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  // Vérification de la configuration Stripe au début du rendu
  if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
    return (
      <div className="min-h-screen bg-slate-900 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-900/50 border-l-4 border-red-500 p-6 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <FiAlertCircle className="h-6 w-6 text-red-400" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-red-200">Erreur de configuration</h3>
                <div className="mt-2 text-sm text-red-300">
                  <p>La clé publique Stripe n'est pas configurée.</p>
                  <p className="mt-1">Veuillez vérifier votre fichier .env et redémarrer l'application.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Rendu du composant
  if (stripeError) {
    return (
      <div className="min-h-screen bg-slate-900 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-900/50 border-l-4 border-red-500 p-6 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <FiAlertCircle className="h-6 w-6 text-red-400" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-red-200">Erreur de connexion à Stripe</h3>
                <div className="mt-2 text-sm text-red-300">
                  <p>Impossible de se connecter au service de paiement.</p>
                  <p className="mt-2">Veuillez :</p>
                  <ul className="list-disc pl-5 mt-1 space-y-1">
                    <li>Désactiver temporairement votre bloqueur de publicités pour ce site</li>
                    <li>Vérifier votre connexion internet</li>
                    <li>Réessayer dans une fenêtre de navigation privée</li>
                  </ul>
                  <button 
                    onClick={() => window.location.reload()}
                    className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium"
                  >
                    Réessayer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-4 md:p-8 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-amber-600/10"></div>
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,rgba(251,191,36,0.1),transparent_50%)]"></div>
        </div>
        
        <div className="max-w-7xl mx-auto space-y-8 relative z-10">
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6"
          >
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <FiDollarSign className="w-6 h-6 text-white" />
                </div>
            <div>
                  <h1 className="text-4xl font-bold text-white tracking-tight">Gestion des Paiements</h1>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="w-2 h-2 bg-gradient-to-r from-green-400 to-green-600 rounded-full"></div>
                    <p className="text-green-400 font-semibold">Centre financier déménageur</p>
            </div>
                </div>
              </div>
              <p className="text-slate-300 mt-3">Suivez vos revenus, gérez vos virements et consultez votre historique financier.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-6 py-3 bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-600 hover:to-slate-700 text-white font-bold rounded-2xl flex items-center gap-2 transition-all duration-300 border border-slate-600/50"
                onClick={() => {
                  // Fonction d'export à implémenter
                  toast.info('Fonctionnalité d\'export à venir');
                }}
              >
                <FiDownload className="w-4 h-4" /> 
                Exporter
              </motion.button>
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-6 py-3 bg-gradient-to-r from-amber-500 via-amber-600 to-yellow-600 hover:from-amber-600 hover:via-amber-700 hover:to-yellow-700 text-slate-900 font-bold rounded-2xl flex items-center gap-2 transition-all duration-300 shadow-xl hover:shadow-amber-500/25 border-2 border-amber-400/20 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => setShowPayoutModal(true)}
                disabled={!paymentMethods.length}
              >
                <FiPlus className="w-4 h-4" /> 
                Demander un virement
              </motion.button>
            </div>
          </motion.header>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard 
            icon={<FiDollarSign className="w-6 h-6 text-white" />} 
            label="Revenus totaux" 
            value={formatTND(paymentStats.totalEarned)} 
            trend={`${paymentStats.completedJobs} missions`} 
            gradient="from-green-500 to-green-600"
            delay={0.1}
          />
          <StatCard 
            icon={<FiClock className="w-6 h-6 text-white" />} 
            label="En attente de virement" 
            value={formatTND(paymentStats.pendingPayouts)} 
            trend={summaryData.nextPayoutDate !== '--/--/----' ? `Prochain virement: ${summaryData.nextPayoutDate}` : 'Aucun virement programmé'} 
            gradient="from-amber-500 to-amber-600"
            delay={0.2}
          />
          <StatCard 
            icon={<FiCheckCircle className="w-6 h-6 text-white" />} 
            label="Missions terminées" 
            value={paymentStats.completedJobs} 
            trend="Ce mois-ci" 
            gradient="from-blue-500 to-blue-600"
            delay={0.3}
          />
          <StatCard 
            icon={<FiTrendingUp className="w-6 h-6 text-white" />} 
            label="Revenus ce mois" 
            value={formatTND(summaryData.monthlyRevenue)} 
            trend="+12.5% vs mois dernier" 
            gradient="from-purple-500 to-purple-600"
            delay={0.4}
          />
        </div>
        
        {/* Section Méthodes de paiement */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-xl border border-slate-700/50 rounded-3xl shadow-2xl overflow-hidden"
        >
          <div className="p-6 border-b border-gradient-to-r from-slate-700/80 via-blue-500/20 to-slate-700/80 bg-gradient-to-r from-slate-800/50 to-slate-900/30 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg ring-2 ring-blue-500/20">
                <FiCreditCard className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white tracking-wide">Méthodes de Paiement</h3>
                <p className="text-sm text-blue-300/80 mt-1 font-medium">{paymentMethods.length} carte{paymentMethods.length !== 1 ? 's' : ''} enregistrée{paymentMethods.length !== 1 ? 's' : ''}</p>
              </div>
            </div>
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowAddMethodModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-amber-500 via-amber-600 to-amber-700 hover:from-amber-600 hover:via-amber-700 hover:to-amber-800 text-white font-bold rounded-2xl flex items-center gap-2 transition-all duration-300 shadow-xl hover:shadow-amber-500/25 border-2 border-amber-400/20"
            >
              <FiPlus className="w-4 h-4" /> 
              Ajouter une carte
            </motion.button>
          </div>
          
          <div className="p-6">
            {paymentMethods.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paymentMethods.map((method, index) => (
                  <motion.div 
                    key={method.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="bg-gradient-to-br from-slate-700/80 to-slate-800/80 border border-slate-600/50 rounded-2xl p-5 hover:border-slate-500/70 transition-all duration-300 group"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg ${
                          method.card?.brand?.toLowerCase() === 'visa' ? 'bg-gradient-to-br from-blue-500 to-blue-600' :
                          method.card?.brand?.toLowerCase() === 'mastercard' ? 'bg-gradient-to-br from-orange-500 to-red-500' :
                          'bg-gradient-to-br from-slate-600 to-slate-700'
                        }`}>
                          {method.card?.brand ? (
                            <span className="font-bold text-white text-sm">{method.card.brand.substring(0, 2).toUpperCase()}</span>
                          ) : (
                            <FiCreditCard className="w-6 h-6 text-white" />
                          )}
                        </div>
                        <div>
                          <p className="font-bold text-white">
                            {method.card?.brand ? `Carte ${method.card.brand}` : 'Carte bancaire'}
                          </p>
                          <p className="text-sm text-slate-400 font-mono">
                            •••• •••• •••• {method.card?.last4 || '****'}
                          </p>
                        </div>
                      </div>
                      {method.isDefault && (
                        <span className="px-3 py-1.5 text-xs font-bold bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-400 border border-green-500/30 rounded-full">
                          Par défaut
                        </span>
                      )}
                    </div>
                    <div className="pt-4 border-t border-slate-600/50 flex justify-between items-center text-sm">
                      <span className="text-slate-400 font-medium">Expire: {method.card?.expMonth}/{method.card?.expYear?.toString().slice(-2)}</span>
                      <motion.button 
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="text-amber-400 hover:text-amber-300 font-semibold transition-colors"
                      >
                        Supprimer
                      </motion.button>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="mx-auto w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mb-4">
                  <FiCreditCard className="w-8 h-8 text-amber-400" />
                </div>
                <h4 className="text-lg font-medium text-white mb-2">Aucune méthode de paiement</h4>
                <p className="text-slate-400 mb-6">Ajoutez une carte pour recevoir vos paiements</p>
                <button 
                  onClick={() => setShowAddMethodModal(true)}
                  className="px-6 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-500 flex items-center gap-2 mx-auto"
                >
                  <FiPlus className="w-4 h-4" /> Ajouter une carte
                </button>
              </div>
            )}
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-xl border border-slate-700/50 rounded-3xl shadow-2xl overflow-hidden"
        >
          <div className="p-6 border-b border-gradient-to-r from-slate-700/80 via-purple-500/20 to-slate-700/80 bg-gradient-to-r from-slate-800/50 to-slate-900/30">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg ring-2 ring-purple-500/20">
                  <FiBarChart2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white tracking-wide">Historique des Transactions</h3>
                  <p className="text-sm text-purple-300/80 mt-1 font-medium">{filteredTransactions.length} transaction{filteredTransactions.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="px-4 py-2 bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-600 hover:to-slate-700 text-white rounded-xl transition-all duration-300 border border-slate-600/50 flex items-center gap-2"
                >
                  <FiRefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                  Actualiser
                </motion.button>
              </div>
            </div>

            
            {/* Filtres */}
            <div className="space-y-4">
              <div className="relative">
                <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Rechercher par référence..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-gradient-to-r from-slate-700/80 to-slate-800/80 border-2 border-slate-600/50 rounded-2xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all duration-300"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Statut</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-4 py-3 bg-gradient-to-r from-slate-700/80 to-slate-800/80 border-2 border-slate-600/50 rounded-2xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all duration-300"
                  >
                    <option value="all">Tous les statuts</option>
                    <option value="Payé">Payé</option>
                    <option value="En attente">En attente</option>
                    <option value="Échoué">Échoué</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Type</label>
                  <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className="w-full px-4 py-3 bg-gradient-to-r from-slate-700/80 to-slate-800/80 border-2 border-slate-600/50 rounded-2xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all duration-300"
                  >
                    <option value="all">Tous les types</option>
                    <option value="paiement">Paiement</option>
                    <option value="payout">Virement</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Période</label>
                  <select
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="w-full px-4 py-3 bg-gradient-to-r from-slate-700/80 to-slate-800/80 border-2 border-slate-600/50 rounded-2xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all duration-300"
                  >
                    <option value="all">Toutes les dates</option>
                    <option value="today">Aujourd'hui</option>
                    <option value="week">Cette semaine</option>
                   <option value="month">Ce mois</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="border-b border-slate-700">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Référence</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Montant</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Statut</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                <AnimatePresence>
                  {loading ? (
                    <LoadingSkeleton count={5} />
                  ) : filteredTransactions.length > 0 ? (
                    filteredTransactions.map((tx, index) => (
                      <motion.tr 
                        key={tx.id}
                        layout
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        className="hover:bg-gradient-to-r hover:from-slate-700/30 hover:to-slate-800/30 cursor-pointer transition-all duration-300"
                        onClick={() => navigate(`/demenageur/transactions/${tx.id}`)}
                      >
                        <td className="px-6 py-4">
                          <p className="font-semibold text-white">{tx.reference || '--'}</p>
                          <p className="text-xs text-slate-400 font-mono">ID: {tx.id}</p>
                        </td>
                        <td className="px-6 py-4 text-slate-300 whitespace-nowrap">
                          {tx.createdAt ? formatDate(tx.createdAt) : '--'}
                        </td>
                        <td className="px-6 py-4 text-white font-semibold">
                          {tx.amount >= 0 ? (
                            <span className="text-green-400">+{formatTND(tx.amount)}</span>
                          ) : (
                            <span className="text-red-400">{formatTND(tx.amount)}</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400">
                            {tx.type === 'payout' ? 'Virement' : 'Paiement'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge status={tx.status} />
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/demenageur/transactions/${tx.id}`);
                            }}
                            className="p-2 text-slate-400 hover:text-amber-400 hover:bg-slate-700 rounded-lg"
                            title="Voir les détails"
                          >
                            <FiArrowRight className="w-4 h-4" />
                          </button>
                        </td>
                      </motion.tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center">
                        <div className="mx-auto w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mb-4">
                          <FiDollarSign className="w-8 h-8 text-slate-400" />
                        </div>
                        <h4 className="text-lg font-medium text-white mb-1">Aucune transaction</h4>
                        <p className="text-slate-400">Vos transactions apparaîtront ici</p>
                      </td>
                    </tr>
                  )}
                </AnimatePresence>
              </tbody>
            </table>
            
            {/* Pagination */}
            {transactions.length > 0 && (
              <div className="p-4 border-t border-slate-700 flex flex-col sm:flex-row items-center justify-between text-sm text-slate-400">
                <div className="mb-2 sm:mb-0">
                  Affichage de {(pagination.page - 1) * pagination.limit + 1} à{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} sur {pagination.total} transactions
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="px-3 py-1 border border-slate-600 rounded-md hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Précédent
                  </button>
                  <span className="px-3 py-1">
                    Page {pagination.page} sur {pagination.totalPages}
                  </span>
                  <button 
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                    className="px-3 py-1 border border-slate-600 rounded-md hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}
          </div>
      </motion.div>
      {/* Modale d'ajout de méthode de paiement */}
      <AddPaymentMethodModal 
        isOpen={showAddMethodModal}
        onClose={() => setShowAddMethodModal(false)}
        onSuccess={async (paymentMethod) => {
          // Recharger les méthodes de paiement
          await loadData();
          
          // Vérifier si on vient de la sélection d'un abonnement
          const urlParams = new URLSearchParams(location.search);
          const subscriptionPlan = urlParams.get('subscription_plan');
          
          if (subscriptionPlan) {
            try {
              // Appeler l'API pour activer l'abonnement avec la nouvelle méthode de paiement
              const response = await fetch('/api/subscriptions/start', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                  plan: subscriptionPlan,
                  paymentMethodId: paymentMethod.id
                })
              });

              if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Erreur lors de l\'activation de l\'abonnement');
              }

              const data = await response.json();
              console.log('Abonnement activé avec succès:', data);
              toast.success('Votre abonnement a été activé avec succès !');

              // Rediriger vers le tableau de bord après un court délai
              setTimeout(() => {
                navigate('/demenageur');
              }, 2000);

            } catch (error) {
              console.error('Erreur lors de l\'activation de l\'abonnement:', error);
              toast.error(`Erreur: ${error.message || 'Impossible d\'activer l\'abonnement'}`);
              
              // Rediriger quand même vers le tableau de bord mais avec un message d'erreur
              setTimeout(() => {
                navigate('/demenageur');
              }, 3000);
            }
          }
        }}
      />
      
      {/* Modale de demande de virement */}
      <AnimatePresence>
        {showPayoutModal && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowPayoutModal(false)}
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="relative bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-xl border border-slate-700/50 rounded-3xl shadow-2xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gradient-to-r from-slate-700/80 via-amber-500/20 to-slate-700/80 bg-gradient-to-r from-slate-800/50 to-slate-900/30">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <FiDollarSign className="w-5 h-5 text-white" />
                  </div>
                  <div>
                <h3 className="text-xl font-bold text-white">Demander un virement</h3>
                <p className="text-slate-400 text-sm mt-1">Transférez votre solde vers votre compte bancaire</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                {paymentMethods.length > 0 ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Montant à transférer (TND)
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          value={payoutAmount}
                          onChange={(e) => setPayoutAmount(e.target.value)}
                          min="10"
                          step="0.01"
                          className="w-full px-4 py-3 bg-gradient-to-r from-slate-700/80 to-slate-800/80 border-2 border-slate-600/50 rounded-2xl text-white focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all duration-300"
                          placeholder="0.00"
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 font-semibold">TND</span>
                      </div>
                      <p className="mt-1 text-xs text-slate-400">
                        Solde disponible: {formatTND(summaryData.monthlyRevenue - summaryData.pendingPayout)}
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Méthode de paiement
                      </label>
                      <select
                        value={selectedMethod}
                        onChange={(e) => setSelectedMethod(e.target.value)}
                        className="w-full px-4 py-3 bg-gradient-to-r from-slate-700/80 to-slate-800/80 border-2 border-slate-600/50 rounded-2xl text-white focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all duration-300"
                      >
                        <option value="">Sélectionnez une méthode</option>
                        {paymentMethods.map(method => (
                          <option key={method.id} value={method.id}>
                            Carte {method.card?.brand} •••• {method.card?.last4}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="pt-4">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handleRequestPayout}
                        disabled={!payoutAmount || !selectedMethod || processingPayout}
                        className="w-full py-4 bg-gradient-to-r from-amber-500 via-amber-600 to-amber-700 hover:from-amber-600 hover:via-amber-700 hover:to-amber-800 text-white font-bold rounded-2xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all duration-300 shadow-xl hover:shadow-amber-500/25 border-2 border-amber-400/20"
                      >
                        {processingPayout ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Traitement...
                          </>
                        ) : (
                          'Demander le virement'
                        )}
                      </motion.button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-6">
                    <div className="mx-auto w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mb-4">
                      <FiAlertCircle className="w-8 h-8 text-amber-400" />
                    </div>
                    <h4 className="text-lg font-medium text-white mb-2">Aucune méthode de paiement</h4>
                    <p className="text-slate-400 mb-6">Vous devez d'abord ajouter une carte pour effectuer un virement</p>
                    <motion.button 
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        setShowPayoutModal(false);
                        setShowAddMethodModal(true);
                      }}
                      className="px-6 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-500 flex items-center gap-2 mx-auto transition-all duration-300"
                    >
                      <FiPlus className="w-4 h-4" /> Ajouter une carte
                    </motion.button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
      <ToastContainer 
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
      </div>
    </Elements>
)}